/**
 * command-profile.schema.ts - Command Profile Schema for Registr
 * 
 * CHANGE HISTORY:
 * - 251116_2353 danielbeer: Create Registr schema for command profiles
 * 
 * Defines the schema for command profiles following Registr standards.
 */

import type { RegistrBaseSchema } from '@opcentral/registr';

export const commandProfileSchema: RegistrBaseSchema = {
  $schema: 'https://opcentral.dev/schemas/registr/v1',
  $id: 'command-profile',
  version: '1.0.0',
  type: 'command-profile',
  
  fields: [
    {
      name: 'id',
      type: 'string',
      required: true,
      description: 'Unique profile identifier',
      validation: {
        pattern: '^[a-z][a-z0-9-]*$',
        minLength: 3,
        maxLength: 50
      }
    },
    {
      name: 'name',
      type: 'string',
      required: true,
      description: 'Human-readable profile name',
      validation: {
        minLength: 3,
        maxLength: 100
      }
    },
    {
      name: 'description',
      type: 'string',
      required: true,
      description: 'Detailed description of the profile',
      validation: {
        minLength: 10,
        maxLength: 500
      }
    },
    {
      name: 'version',
      type: 'semver',
      required: true,
      description: 'Semantic version of the profile'
    },
    {
      name: 'commands',
      type: 'array',
      required: true,
      description: 'Command definitions',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string', required: true },
          action: { type: 'string', required: true },
          keys: { type: 'array', items: { type: 'string' }, required: true },
          label: { type: 'string', required: true },
          description: { type: 'string', required: true },
          scope: { type: 'string' },
          preventDefault: { type: 'boolean' },
          allowInInput: { type: 'boolean' },
          category: { type: 'string' }
        }
      }
    },
    {
      name: 'contexts',
      type: 'object',
      required: false,
      description: 'Context-specific command overrides'
    },
    {
      name: 'metadata',
      type: 'object',
      required: false,
      description: 'Additional metadata',
      properties: {
        author: { type: 'string' },
        created: { type: 'date' },
        lastUpdated: { type: 'date' },
        tags: { type: 'array', items: { type: 'string' } }
      }
    }
  ],
  
  validation: {
    rules: [
      {
        name: 'unique-command-ids',
        type: 'entry',
        severity: 'error',
        condition: 'hasDuplicateCommandIds(data.commands)',
        message: 'Command IDs must be unique within a profile'
      },
      {
        name: 'unique-actions',
        type: 'entry',
        severity: 'warning',
        condition: 'hasDuplicateActions(data.commands)',
        message: 'Multiple commands with the same action may cause conflicts'
      }
    ],
    strict: true,
    allowAdditional: false
  },
  
  indexes: [
    {
      name: 'by-id',
      fields: ['id'],
      unique: true
    },
    {
      name: 'by-version',
      fields: ['version']
    }
  ],
  
  views: [
    {
      name: 'all-profiles',
      description: 'All command profiles',
      filter: {},
      sort: { name: 1 }
    },
    {
      name: 'by-tag',
      description: 'Profiles filtered by tag',
      filter: { 'metadata.tags': { $in: ['{{tag}}'] } }
    }
  ],
  
  documentation: {
    required: ['description', 'commands'],
    recommended: ['metadata.author', 'metadata.tags'],
    coverage: {
      minimum: 80,
      target: 100
    }
  },
  
  metadata: {
    name: 'Command Profile',
    description: 'Schema for keyboard command profiles',
    version: '1.0.0',
    author: 'OpCentral',
    created: '2025-11-16',
    lastUpdated: '2025-11-16',
    tags: ['command', 'keyboard', 'profile', 'navigation']
  }
};
